<template>
  <div class="maintenance-mode-page">
    <!-- No navigation menu - just the maintenance content -->
    <div class="container flex-grow-1 d-flex align-items-center">
      <div class="row justify-content-center w-100">
        <div class="col-md-8 col-lg-6">
          <div class="maintenance-card">
            <div class="maintenance-logo">
              <img src="@/assets/img/quitly-blue.svg" alt="Quitly" class="logo" />
            </div>

            <div class="maintenance-icon">
              <font-awesome-icon icon="tools" size="4x" />
            </div>

            <h1 class="maintenance-title">Wartungsmodus</h1>

            <div class="maintenance-message">
              <p v-if="isMaintenanceMode && maintenanceMessage">{{ maintenanceMessage }}</p>
              <p v-else-if="isScheduledMaintenance">
                Ein geplanter Wartungsmodus ist aktiv. Die Anwendung wird voraussichtlich um
                {{ formatEndTime(scheduledMaintenanceEnd) }} wieder verfügbar sein.
              </p>
              <p v-else-if="isMaintenanceMode">
                Die Anwendung befindet sich derzeit im Wartungsmodus. Bitte versuche es später
                erneut.
              </p>
              <p v-else>
                Wartungsmodus wurde deaktiviert. Du wirst zur Dashboard-Seite weitergeleitet...
              </p>
            </div>

            <div v-if="isScheduledMaintenance" class="maintenance-schedule">
              <div class="schedule-info">
                <font-awesome-icon icon="calendar-alt" class="me-2" />
                <span class="fw-semibold">Geplanter Wartungsmodus</span>
              </div>
              <div class="schedule-times">
                <div class="time-item">
                  <span class="time-label">Start:</span>
                  <span class="time-value">{{ formatTime(scheduledMaintenanceStart) }}</span>
                </div>
                <div class="time-item">
                  <span class="time-label">Ende:</span>
                  <span class="time-value">{{ formatTime(scheduledMaintenanceEnd) }}</span>
                </div>
              </div>
            </div>

            <div class="maintenance-info">
              <p class="text-muted">
                <font-awesome-icon icon="clock" class="me-2" />
                Wir arbeiten an Verbesserungen und sind bald wieder für dich da.
              </p>
            </div>

            <div class="maintenance-actions">
              <button class="btn btn-primary" @click="refreshPage">
                <font-awesome-icon icon="refresh" class="me-2" />
                Seite aktualisieren
              </button>
              <button v-if="isAuthenticated" class="btn btn-outline-secondary ms-2" @click="handleLogout">
                <font-awesome-icon icon="sign-out-alt" class="me-2" />
                Abmelden
              </button>
            </div>

            <div class="maintenance-footer">
              <p class="text-muted small">Bei Fragen wende dich an unseren Support.</p>
              
              <div class="footer-links">
                <div class="footer-copyright">
                  <span>&copy; {{ new Date().getFullYear() }} Quitly. Alle Rechte vorbehalten.</span>
                </div>
                <div class="footer-links-list">
                  <a href="https://quitly.de/datenschutz/" class="footer-link" target="_blank">
                    Datenschutz
                  </a>
                  <a href="https://quitly.de/datenschutz/agb/" class="footer-link" target="_blank">
                    Nutzungsbedingungen
                  </a>
                  <a href="https://quitly.de/datenschutz/widerrufsbelehrung/" class="footer-link" target="_blank">
                    Widerrufsbelehrung
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, watch, computed } from 'vue'
import { useRouter } from 'vue-router'
import { maintenanceApi } from '@/api/maintenance'
import { useAuthStore } from '@/stores/auth'

// Stores
const authStore = useAuthStore()

// State
const isMaintenanceMode = ref(false)
const maintenanceMessage = ref('')
const isScheduledMaintenance = ref(false)
const scheduledMaintenanceStart = ref('')
const scheduledMaintenanceEnd = ref('')

// Computed
const isAuthenticated = computed(() => authStore.isAuthenticated)

// Methods
const loadMaintenanceStatus = async () => {
  try {
    const response = await maintenanceApi.getStatus()

    isMaintenanceMode.value = response.isMaintenanceMode || false
    maintenanceMessage.value = response.maintenanceMessage || ''

    // Check for scheduled maintenance
    if (response.scheduledMaintenance && response.scheduledMaintenance.isActive) {
      isScheduledMaintenance.value = true
      scheduledMaintenanceStart.value = response.scheduledMaintenance.start
      scheduledMaintenanceEnd.value = response.scheduledMaintenance.end
    } else {
      isScheduledMaintenance.value = false
    }
  } catch (error) {
    console.error('Error loading maintenance status:', error)
  }
}

const formatTime = (dateString) => {
  if (!dateString) return ''

  const date = new Date(dateString)
  return date.toLocaleString('de-DE', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}

const formatEndTime = (dateString) => {
  if (!dateString) return ''

  const date = new Date(dateString)
  return date.toLocaleTimeString('de-DE', {
    hour: '2-digit',
    minute: '2-digit',
  })
}

const refreshPage = () => {
  window.location.reload()
}

const handleLogout = async () => {
  try {
    await authStore.logout()
  } catch (error) {
    console.error('Fehler beim Logout:', error)
  }
}

// Lifecycle
onMounted(() => {
  loadMaintenanceStatus()

  // Check maintenance status every 30 seconds
  setInterval(loadMaintenanceStatus, 30000)
})

// Initialize router
const router = useRouter()

// Watch maintenance mode status
watch(
  [isMaintenanceMode, isScheduledMaintenance],
  ([maintenanceMode, scheduledMaintenance]) => {
    // If neither maintenance mode nor scheduled maintenance is active, redirect to dashboard
    if (!maintenanceMode && !scheduledMaintenance) {
      router.push('/dashboard')
    }
  },
  { immediate: false },
)
</script>

<style scoped>
.maintenance-mode-page {
  min-height: 100vh;
  width: 100vw;
  background: linear-gradient(135deg, var(--quitly-primary) 0%, var(--quitly-gradient-end) 100%);
  display: flex;
  flex-direction: column;
  position: fixed;
  top: 0;
  left: 0;
  z-index: 9999;
  padding: 2rem 0;
}

.maintenance-card {
  background: white;
  border-radius: 1rem;
  padding: 3rem 2rem;
  box-shadow: 0 20px 40px var(--quitly-shadow-color);
  text-align: center;
  border: 1px solid var(--border-light);
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.maintenance-logo {
  margin-bottom: 2rem;
}

.maintenance-logo .logo {
  height: 60px;
  width: auto;
}

.maintenance-icon {
  color: var(--quitly-primary);
  margin-bottom: 2rem;
}

.maintenance-title {
  color: var(--text-primary);
  font-weight: 700;
  margin-bottom: 1.5rem;
  font-size: 2.5rem;
  font-family: var(--quitly-heading-font);
}

.maintenance-message {
  font-size: 1.1rem;
  color: var(--text-secondary);
  margin-bottom: 2rem;
  line-height: 1.6;
}

.maintenance-schedule {
  background: var(--quitly-light);
  border-radius: 0.5rem;
  padding: 1.5rem;
  margin-bottom: 2rem;
  border-left: 4px solid var(--quitly-warning);
}

.schedule-info {
  color: var(--quitly-warning);
  font-weight: 600;
  margin-bottom: 1rem;
  font-size: 1.1rem;
}

.schedule-times {
  display: flex;
  justify-content: space-around;
  gap: 1rem;
}

.time-item {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.time-label {
  font-size: 0.9rem;
  color: #6c757d;
  margin-bottom: 0.25rem;
}

.time-value {
  font-weight: 600;
  color: var(--text-primary);
  font-size: 1rem;
}

.maintenance-info {
  margin-bottom: 2rem;
}

.maintenance-actions {
  margin-bottom: 2rem;
}

.maintenance-footer {
  border-top: 1px solid var(--border-light);
  padding-top: 1.5rem;
  margin-top: auto;
}

.footer-links {
  margin-top: 1rem;
}

.footer-copyright {
  margin-bottom: 1rem;
  text-align: center;
  color: var(--text-muted);
  font-size: 0.9rem;
}

.footer-links-list {
  display: flex;
  flex-direction: row;
  gap: 1.5rem;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
}

.btn-primary {
  background: linear-gradient(135deg, var(--quitly-primary), var(--quitly-gradient-end));
  border: none;
  padding: 0.75rem 2rem;
  font-weight: 600;
  border-radius: 0.5rem;
  transition: all 0.3s ease;
  color: white;
}

.btn-primary:hover {
  background: linear-gradient(135deg, var(--quitly-primary-hover), var(--quitly-gradient-end));
  transform: translateY(-2px);
  box-shadow: 0 4px 12px var(--quitly-shadow-hover-color);
  color: white;
}

.footer-link {
  color: var(--text-muted);
  text-decoration: none;
  font-size: 0.9rem;
  transition: color 0.3s ease;
}

.footer-link:hover {
  color: var(--quitly-primary);
  text-decoration: none;
}

@media (max-width: 768px) {
  .maintenance-card {
    padding: 2rem 1.5rem;
  }

  .maintenance-title {
    font-size: 2rem;
  }

  .schedule-times {
    flex-direction: column;
    gap: 0.5rem;
  }
}
</style>
